import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import * as St from './index.styled';
import CustomBlockButton from '../../../../../components/button/CustomBlockButton';
import { CustomRatioImage } from '../../../../../components/image/CustomRatioImage';
import { CustomDialog } from '../../../../../components/dialog/v1/CustomDialog';
import { useApiInfoContextValueHook } from '../../../../../contexts/ApiInfoContextProvider';
import { SellertoolSignatureUtils } from '../../../../../utils/sellertool-signature-utils';
import { CustomBackdropController } from '../../../../../components/backdrop/default/v1';
import axios from 'axios';
import { customToast } from '../../../../../components/toast/custom-react-toastify/v1';
import { useReactToPrint } from 'react-to-print';
import { WegetBulkSelectModal } from '../weget-bulk-select-modal';

const API_HOST = process.env.REACT_APP_MAIN_API_HOST;

// 확정 축 필터에서 '미확정' 버킷을 가리키는 센티널 값
const UNCONFIRMED_KEY = '__UNCONFIRMED__';

// 제품리스트 탭.
// - 표시 데이터: '파레트 준비'의 로드아이템(pallets/loadItems = 준비).
// - "파레트 지정" = 확정 파레트에 배정 → 확정 처리(단일 원본 모델의 confirmed 필드 세팅).
// - 각 행에 준비 파레트 번호 / 확정 파레트 번호를 함께 표기.
export function WegetViewerItemFirst({
    pallets,
    loadItems,
    stageUUID,
    onReqFetch,
    onReqFetchAll,
    // 확정 파레트 목록(배정 대상)
    confirmedPallets,
    // 준비 로드아이템 갱신용. 기본 'pallets'.
    apiResource = 'pallets'
}) {
    const apiInfoContextValueHook = useApiInfoContextValueHook();

    const [searchQuery, setSearchQuery] = useState('');
    const [isScanningMode, setIsScanningMode] = useState(false);
    // 요약 카드 기준 축. 'PREPARE'(준비 파레트) | 'CONFIRM'(확정 파레트)
    const [summaryAxis, setSummaryAxis] = useState('PREPARE');
    // 준비/확정 필터는 각각 따로 보관하고 둘 다 AND로 적용한다.
    const [selectedPalletUUIDs, setSelectedPalletUUIDs] = useState([]);
    const [selectedConfirmedPalletUUIDs, setSelectedConfirmedPalletUUIDs] = useState([]);
    const [selectPalletModalOpen, setSelectPalletModalOpen] = useState(false);

    const [printModeModalOpen, setPrintModeModalOpen] = useState(false);

    // 제품리스트 다중 선택 → 확정 파레트 일괄 배정
    const [selectedItemUUIDs, setSelectedItemUUIDs] = useState(() => new Set());
    // 확정 지정 모달 (대상 아이템 배열을 담아 확정 파레트를 고른다)
    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [assignTargetItems, setAssignTargetItems] = useState([]);
    // 동일 품목 일괄 선택 모달의 기준 아이템
    const [bulkSelectTarget, setBulkSelectTarget] = useState(null);

    useEffect(() => {
        let barcode = '';
        let barcodeTimer;

        const handleKeyPress = (e) => {
            if (barcodeTimer) {
                clearTimeout(barcodeTimer);
            }
            if (e.key === 'Enter') {
                setSearchQuery(barcode);
                barcode = '';
                return;
            }
            barcode += e.key;
            barcodeTimer = setTimeout(() => {
                barcode = '';
            }, 100);
        };

        if (!isScanningMode) {
            return () => window.removeEventListener('keypress', handleKeyPress);
        }

        window.addEventListener('keypress', handleKeyPress);

        return () => {
            window.removeEventListener('keypress', handleKeyPress);
        };
    }, [isScanningMode]);

    const handleChangeIsScanningMode = (bool) => {
        setIsScanningMode(bool);
    }

    const handleChangeSearchQuery = (e) => {
        const value = e.target.value;
        setSearchQuery(value);
    }

    const handleClearSearchQuery = () => {
        setSearchQuery('');
    }

    const handleOpenSelectPalletUUIDModal = () => {
        setSelectPalletModalOpen(true);
    }

    const handleCloseSelectPalletUUIDModal = () => {
        setSelectPalletModalOpen(false);
    }

    const handlePushSelectedPalletUUID = (addUUID) => {
        setSelectedPalletUUIDs(prev => prev.includes(addUUID) ? prev?.filter(r => r !== addUUID) : prev.concat(addUUID));
    }

    const handlePopSelectedPalletUUID = (removedUUID) => {
        setSelectedPalletUUIDs(prev => {
            return prev?.filter(r => r !== removedUUID);
        })
    }

    const handlePushSelectedConfirmedPalletUUID = (addUUID) => {
        setSelectedConfirmedPalletUUIDs(prev => prev.includes(addUUID) ? prev?.filter(r => r !== addUUID) : prev.concat(addUUID));
    }

    const handlePopSelectedConfirmedPalletUUID = (removedUUID) => {
        setSelectedConfirmedPalletUUIDs(prev => {
            return prev?.filter(r => r !== removedUUID);
        })
    }

    const handleClearAllFilters = () => {
        setSelectedPalletUUIDs([]);
        setSelectedConfirmedPalletUUIDs([]);
    }

    const buildSignatureHeaders = () => {
        const timestamp = Date.now().toString();
        const signature = SellertoolSignatureUtils().generateSigniture({
            apiKey: apiInfoContextValueHook?.apiInfo?.apiKey,
            secretKey: apiInfoContextValueHook?.apiInfo?.secretKey,
            timestamp: timestamp
        })
        return {
            'x-sellertool-apiKey': apiInfoContextValueHook?.apiInfo?.apiKey,
            'x-sellertool-timestamp': timestamp,
            'x-sellertool-signiture': signature
        }
    }

    const refetch = () => (onReqFetchAll || onReqFetch)();

    // 낮은 레벨 요청 (fetch/모달제어는 호출부에서). 실패 시 undefined 반환.
    const reqAddPallet = async (newPallet) => {
        const result = await axios.post(`${API_HOST}/${apiResource}/add`, { pallet: newPallet }, {
            headers: buildSignatureHeaders(),
        })
            .then(res => ({ res: res, content: res.data?.content }))
            .catch(err => { customToast.error(err.response); })
        return result?.content;
    }

    // 확정 파레트 신규 생성
    const reqAddConfirmedPallet = async (newPallet) => {
        const result = await axios.post(`${API_HOST}/confirmed-pallets/add`, { pallet: newPallet }, {
            headers: buildSignatureHeaders(),
        })
            .then(res => ({ res: res, content: res.data?.content }))
            .catch(err => { customToast.error(err.response); })
        return result?.content;
    }

    // 준비 로드아이템 일괄 갱신 (확정 필드 포함 시 확정 지정/해제도 처리됨)
    const reqBulkUpdateLoadItems = async (newLoadItems) => {
        const result = await axios.put(`${API_HOST}/${apiResource}/load-items/bulk-update`, { palletLoadItems: newLoadItems }, {
            headers: buildSignatureHeaders(),
        })
            .then(res => ({ res: res, content: res.data?.content }))
            .catch(err => { customToast.error(err.response); })
        return result?.content;
    }

    const buildNextPalletNumber = () => {
        if (!pallets || pallets.length <= 0) {
            return 1;
        }
        return Math.max(...pallets.map(p => Number(p.palletNumber) || 0)) + 1;
    }

    const buildNextConfirmedPalletNumber = () => {
        if (!confirmedPallets || confirmedPallets.length <= 0) {
            return 1;
        }
        return Math.max(...confirmedPallets.map(p => Number(p.palletNumber) || 0)) + 1;
    }

    const buildNewPallet = () => ({
        _id: null,
        uuid: uuidv4(),
        palletNumber: buildNextPalletNumber(),
        type: 'HETEROMORPHIC',
        status: 'LOADING',
        rackLocationAddress: '',
        childRackLocationAddresses: [],
        stageUUID: stageUUID
    })

    // 새 준비 파레트 생성 (요약 카드의 + 버튼)
    const handleCreatePallet = async () => {
        const newPallet = buildNewPallet();
        CustomBackdropController().showBackdrop();
        const content = await reqAddPallet(newPallet);
        if (content) {
            customToast.success(`파레트 ${newPallet.palletNumber} 를 생성했습니다.`);
            onReqFetch();
        }
        CustomBackdropController().hideBackdrop();
    }

    // 새 확정 파레트 생성 (확정 축 요약 카드의 + 버튼)
    const handleCreateConfirmedPallet = async () => {
        const newPallet = {
            _id: null,
            uuid: uuidv4(),
            palletNumber: buildNextConfirmedPalletNumber(),
            type: 'HETEROMORPHIC',
            status: 'LOADING',
            rackLocationAddress: '',
            childRackLocationAddresses: [],
            stageUUID: stageUUID
        };
        CustomBackdropController().showBackdrop();
        const content = await reqAddConfirmedPallet(newPallet);
        if (content) {
            customToast.success(`확정 파레트 ${newPallet.palletNumber} 를 생성했습니다.`);
            refetch();
        }
        CustomBackdropController().hideBackdrop();
    }

    // 다중 선택 제어
    const handleToggleItemCheck = useCallback((loadItem) => {
        setSelectedItemUUIDs(prev => {
            const next = new Set(prev);
            if (next.has(loadItem.uuid)) {
                next.delete(loadItem.uuid);
            } else {
                next.add(loadItem.uuid);
            }
            return next;
        });
    }, []);

    const handleClearItemSelection = () => setSelectedItemUUIDs(new Set());

    // 확정 지정 모달 열기 (대상 아이템 배열 지정)
    const handleOpenAssignModal = (items) => {
        if (!items || items.length <= 0) {
            return;
        }
        setAssignTargetItems(items);
        setAssignModalOpen(true);
    }

    const handleCloseAssignModal = () => {
        setAssignModalOpen(false);
        setAssignTargetItems([]);
    }

    const handleOpenAssignModalForSelected = () => {
        const targetItems = (loadItems || []).filter(li => selectedItemUUIDs.has(li.uuid));
        handleOpenAssignModal(targetItems);
    }

    // 선택 아이템을 확정 파레트로 배정 (isCreateNew면 새 확정 파레트 생성 후 배정)
    const handleAssignToConfirmed = async (targetConfirmedPallet, isCreateNew) => {
        const items = assignTargetItems;
        if (!items || items.length <= 0) {
            return;
        }

        CustomBackdropController().showBackdrop();

        let target = targetConfirmedPallet;
        if (isCreateNew) {
            const newPallet = {
                _id: null,
                uuid: uuidv4(),
                palletNumber: buildNextConfirmedPalletNumber(),
                type: 'HETEROMORPHIC',
                status: 'LOADING',
                rackLocationAddress: '',
                childRackLocationAddresses: [],
                stageUUID: stageUUID
            };
            const addContent = await reqAddConfirmedPallet(newPallet);
            if (!addContent) {
                CustomBackdropController().hideBackdrop();
                return;
            }
            target = newPallet;
        }

        if (!target) {
            CustomBackdropController().hideBackdrop();
            return;
        }

        const newLoadItems = items.map(li => ({
            ...li,
            confirmedYn: 'y',
            confirmedPalletUUID: target.uuid,
            confirmedPalletNumber: target.palletNumber,
        }));

        const content = await reqBulkUpdateLoadItems(newLoadItems);
        if (content) {
            customToast.success(`${items.length}개를 확정 파레트 ${target.palletNumber}(으)로 지정했습니다.`);
            handleClearItemSelection();
            handleCloseAssignModal();
            refetch();
        }
        CustomBackdropController().hideBackdrop();
    }

    // 확정 취소 (준비로 되돌림)
    const handleUnconfirm = async (items) => {
        if (!items || items.length <= 0) {
            return;
        }
        CustomBackdropController().showBackdrop();
        const result = await axios.post(`${API_HOST}/confirmed-pallets/unconfirm`, {
            palletLoadItems: items.map(li => ({ _id: li._id }))
        }, {
            headers: buildSignatureHeaders(),
        })
            .then(res => ({ content: res.data?.content }))
            .catch(err => { customToast.error(err.response); })

        if (result?.content) {
            customToast.success(`${items.length}개의 확정을 취소했습니다.`);
            refetch();
        }
        CustomBackdropController().hideBackdrop();
    }

    const isConfirmAxis = summaryAxis === 'CONFIRM';

    const filteredLoadItems = loadItems?.filter(r => {
        // 준비 파레트 필터. 선택이 없으면 모두 포함
        if (!selectedPalletUUIDs?.length) return true;

        return selectedPalletUUIDs.includes(r.palletUUID);
    })?.filter(r => {
        // 확정 파레트 필터 (준비 필터와 AND로 결합). 선택이 없으면 모두 포함
        if (!selectedConfirmedPalletUUIDs?.length) return true;

        if (r.confirmedYn !== 'y') {
            return selectedConfirmedPalletUUIDs.includes(UNCONFIRMED_KEY);
        }
        return selectedConfirmedPalletUUIDs.includes(r.confirmedPalletUUID);
    })?.filter(r => {
        // 검색어가 특정 필드에 포함되는 경우 필터링
        return (
            r.productName.includes(searchQuery) ||
            r.optionName.includes(searchQuery) ||
            r.optionCode.includes(searchQuery) ||
            r.waybillNumber.includes(searchQuery)
        );
    })?.sort((a, b) => {
        // 미확정(confirmedYn !== 'y')을 우선 정렬 — 실제 작업 대상
        const aConfirmed = a.confirmedYn === 'y';
        const bConfirmed = b.confirmedYn === 'y';
        if (!aConfirmed && bConfirmed) return -1;
        if (aConfirmed && !bConfirmed) return 1;

        // 정렬 기준 파레트도 축을 따라간다 (확정 축이면 확정 파레트 번호순).
        // 미배정(null)은 NaN이 되어 비교자가 불안정해지므로 0으로 정규화.
        const palletA = Number(isConfirmAxis ? a.confirmedPalletNumber : a.palletNumber) || 0;
        const palletB = Number(isConfirmAxis ? b.confirmedPalletNumber : b.palletNumber) || 0;

        // 파레트 넘버 우선 정렬
        if (palletA !== palletB) return palletA - palletB;

        // 이후 productName과 optionName으로 정렬
        const productA = `${a.productName}_${a.optionName}`;
        const productB = `${b.productName}_${b.optionName}`;

        return productA > productB ? -1 : productA < productB ? 1 : 0;
    })

    // 파레트별 요약 통계 (상품 종류 수 / 총 박스수 / 확정 현황)
    const palletStatMap = new Map();
    (pallets || []).forEach(pallet => {
        const items = (loadItems || []).filter(li => li.palletUUID === pallet.uuid);
        const boxedItems = items.filter(li => Number(li.boxQuantity) > 0);
        palletStatMap.set(pallet.uuid, {
            productKinds: new Set(boxedItems.map(li => `${li.productName}__${li.optionName}`)).size,
            totalBoxes: boxedItems.reduce((sum, li) => sum + (Number(li.boxQuantity) || 0), 0),
            boxedCount: boxedItems.length,
            confirmedCount: boxedItems.filter(li => li.confirmedYn === 'y').length,
        });
    });
    const sortedPallets = (pallets || []).slice().sort((a, b) => (Number(a.palletNumber) || 0) - (Number(b.palletNumber) || 0));
    const sortedConfirmedPallets = (confirmedPallets || []).slice().sort((a, b) => (Number(a.palletNumber) || 0) - (Number(b.palletNumber) || 0));

    // 확정 파레트별 요약 (상품 종류 수 / 총 박스수 / 유입된 준비 파레트 수)
    const confirmedStatMap = new Map();
    (confirmedPallets || []).forEach(pallet => {
        const items = (loadItems || []).filter(li => li.confirmedYn === 'y' && li.confirmedPalletUUID === pallet.uuid);
        const boxedItems = items.filter(li => Number(li.boxQuantity) > 0);
        confirmedStatMap.set(pallet.uuid, {
            productKinds: new Set(boxedItems.map(li => `${li.productName}__${li.optionName}`)).size,
            totalBoxes: boxedItems.reduce((sum, li) => sum + (Number(li.boxQuantity) || 0), 0),
            prepPalletCount: new Set(boxedItems.map(li => li.palletUUID).filter(Boolean)).size,
        });
    });

    // '미확정' 버킷 통계 — 확정 축의 첫 카드이자 실제 작업 대상
    const unconfirmedItems = (loadItems || []).filter(li => li.confirmedYn !== 'y');
    const unconfirmedStat = {
        count: unconfirmedItems.length,
        totalBoxes: unconfirmedItems
            .filter(li => Number(li.boxQuantity) > 0)
            .reduce((sum, li) => sum + (Number(li.boxQuantity) || 0), 0),
    };

    const hasAnyFilter = (selectedPalletUUIDs?.length > 0) || (selectedConfirmedPalletUUIDs?.length > 0);

    const allFilteredChecked = (filteredLoadItems?.length ?? 0) > 0
        && filteredLoadItems.every(li => selectedItemUUIDs.has(li.uuid));

    const handleToggleSelectAllFiltered = () => {
        setSelectedItemUUIDs(prev => {
            const next = new Set(prev);
            if (allFilteredChecked) {
                filteredLoadItems.forEach(li => next.delete(li.uuid));
            } else {
                filteredLoadItems.forEach(li => next.add(li.uuid));
            }
            return next;
        });
    };

    // 동일 품목(상품명 + 옵션명) 풀. 현재 필터로 보이는 목록 기준.
    const buildSameProductPool = (loadItem) => {
        if (!loadItem) {
            return [];
        }
        return (filteredLoadItems || []).filter(li =>
            li.productName === loadItem.productName &&
            li.optionName === loadItem.optionName
        );
    }

    const bulkSelectPool = buildSameProductPool(bulkSelectTarget);
    const bulkSelectableItems = bulkSelectPool.filter(li => !selectedItemUUIDs.has(li.uuid));

    const handleOpenBulkSelectModal = useCallback((loadItem) => {
        setBulkSelectTarget(loadItem);
    }, []);

    const handleCloseBulkSelectModal = () => {
        setBulkSelectTarget(null);
    }

    // 입력한 개수만큼 동일 품목을 추가 선택 (목록에 보이는 순서대로)
    const handleConfirmBulkSelect = (count) => {
        const targets = bulkSelectableItems.slice(0, count);
        if (targets.length <= 0) {
            handleCloseBulkSelectModal();
            return;
        }

        setSelectedItemUUIDs(prev => {
            const next = new Set(prev);
            targets.forEach(li => next.add(li.uuid));
            return next;
        });
        customToast.success(`동일 품목 ${targets.length}개를 선택했습니다.`);
        handleCloseBulkSelectModal();
    }

    const handlePrintModeModalOpen = () => {
        setPrintModeModalOpen(true);
    }

    const handlePrintModeModalClose = () => {
        setPrintModeModalOpen(false);
    }

    return (
        <>
            <St.PalletSummary.Container>
                <St.PalletSummary.TitleRow>
                    <St.PalletSummary.Title>
                        파레트 요약 <span>({isConfirmAxis ? `확정 ${confirmedPallets?.length || 0}개` : `준비 ${pallets?.length || 0}개`})</span>
                    </St.PalletSummary.Title>
                    <St.PalletSummary.AxisToggle>
                        <button
                            type='button'
                            className={!isConfirmAxis ? 'active' : ''}
                            onClick={() => setSummaryAxis('PREPARE')}
                        >준비</button>
                        <button
                            type='button'
                            className={isConfirmAxis ? 'active' : ''}
                            onClick={() => setSummaryAxis('CONFIRM')}
                        >확정</button>
                    </St.PalletSummary.AxisToggle>
                </St.PalletSummary.TitleRow>
                <St.PalletSummary.Scroller>
                    {isConfirmAxis ?
                        <>
                            <St.PalletSummary.Card
                                type='button'
                                className={`unconfirmed ${selectedConfirmedPalletUUIDs?.includes(UNCONFIRMED_KEY) ? 'selected' : ''}`}
                                onClick={() => handlePushSelectedConfirmedPalletUUID(UNCONFIRMED_KEY)}
                            >
                                <div className='no'>미확정</div>
                                <div className='stat'><b>{unconfirmedStat.count}</b>건</div>
                                <div className='sub'>박스 {unconfirmedStat.totalBoxes}</div>
                            </St.PalletSummary.Card>
                            {sortedConfirmedPallets.map(pallet => {
                                const stat = confirmedStatMap.get(pallet.uuid) || { productKinds: 0, totalBoxes: 0, prepPalletCount: 0 };
                                const isSelected = selectedConfirmedPalletUUIDs?.includes(pallet.uuid);
                                return (
                                    <St.PalletSummary.Card
                                        key={pallet.uuid}
                                        type='button'
                                        className={isSelected ? 'selected' : ''}
                                        onClick={() => handlePushSelectedConfirmedPalletUUID(pallet.uuid)}
                                    >
                                        <div className='no'>{pallet.palletNumber}</div>
                                        <div className='stat'><b>{stat.productKinds}</b>종</div>
                                        <div className='sub'>박스 {stat.totalBoxes}</div>
                                        {stat.prepPalletCount > 0 &&
                                            <div className='confirm'>준비 {stat.prepPalletCount}곳</div>
                                        }
                                    </St.PalletSummary.Card>
                                );
                            })}
                            <St.PalletSummary.AddCard
                                type='button'
                                className='confirm'
                                onClick={() => handleCreateConfirmedPallet()}
                            >
                                <div className='plus'>＋</div>
                                <div>새 확정</div>
                            </St.PalletSummary.AddCard>
                        </>
                        :
                        <>
                            {sortedPallets.map(pallet => {
                                const stat = palletStatMap.get(pallet.uuid) || { productKinds: 0, totalBoxes: 0, boxedCount: 0, confirmedCount: 0 };
                                const isSelected = selectedPalletUUIDs?.includes(pallet.uuid);
                                return (
                                    <St.PalletSummary.Card
                                        key={pallet.uuid}
                                        type='button'
                                        className={isSelected ? 'selected' : ''}
                                        onClick={() => handlePushSelectedPalletUUID(pallet.uuid)}
                                    >
                                        <div className='no'>{pallet.palletNumber}</div>
                                        <div className='stat'><b>{stat.productKinds}</b>종</div>
                                        <div className='sub'>박스 {stat.totalBoxes}</div>
                                        {stat.boxedCount > 0 &&
                                            <div className='confirm'>확정 {stat.confirmedCount}/{stat.boxedCount}</div>
                                        }
                                    </St.PalletSummary.Card>
                                );
                            })}
                            <St.PalletSummary.AddCard
                                type='button'
                                onClick={() => handleCreatePallet()}
                            >
                                <div className='plus'>＋</div>
                                <div>새 파레트</div>
                            </St.PalletSummary.AddCard>
                        </>
                    }
                </St.PalletSummary.Scroller>
            </St.PalletSummary.Container>
            <St.PalletList.Container>
                <St.Print.Container>
                    <CustomBlockButton
                        type='button'
                        onClick={() => handlePrintModeModalOpen()}
                    >
                        프린트
                    </CustomBlockButton>
                </St.Print.Container>
                <St.PalletList.Wrapper>
                    <St.PalletList.LabelField>선택된 파레트 : </St.PalletList.LabelField>
                    {selectedPalletUUIDs?.map(selectedPalletUUID => {
                        const targetPallet = pallets?.find(r => r.uuid === selectedPalletUUID);
                        return (
                            <St.PalletList.PalletChip
                                key={`prep-${selectedPalletUUID}`}
                                type='button'
                                className='prep'
                                onClick={() => handlePopSelectedPalletUUID(selectedPalletUUID)}
                            >
                                <span className='tag'>준비</span>
                                <span>{targetPallet?.palletNumber ?? '-'}</span>
                                <span className='x'>✕</span>
                            </St.PalletList.PalletChip>
                        );
                    })}
                    {selectedConfirmedPalletUUIDs?.map(selectedKey => {
                        const isUnconfirmed = selectedKey === UNCONFIRMED_KEY;
                        const targetPallet = confirmedPallets?.find(r => r.uuid === selectedKey);
                        return (
                            <St.PalletList.PalletChip
                                key={`confirm-${selectedKey}`}
                                type='button'
                                className={isUnconfirmed ? 'unconfirmed' : 'confirm'}
                                onClick={() => handlePopSelectedConfirmedPalletUUID(selectedKey)}
                            >
                                <span className='tag'>확정</span>
                                <span>{isUnconfirmed ? '미확정' : (targetPallet?.palletNumber ?? '-')}</span>
                                <span className='x'>✕</span>
                            </St.PalletList.PalletChip>
                        );
                    })}
                    {hasAnyFilter &&
                        <St.PalletList.ClearAllButton
                            type='button'
                            onClick={() => handleClearAllFilters()}
                        >
                            전체 해제
                        </St.PalletList.ClearAllButton>
                    }
                    <St.PalletList.AddButtonField>
                        <CustomBlockButton
                            type='button'
                            onClick={() => handleOpenSelectPalletUUIDModal()}
                        >
                            <div>
                                <CustomRatioImage.Figure>
                                    <CustomRatioImage.Image src='/assets/icons/add_default_000000.svg' />
                                </CustomRatioImage.Figure>
                            </div>
                        </CustomBlockButton>
                    </St.PalletList.AddButtonField>
                </St.PalletList.Wrapper>
            </St.PalletList.Container>
            <St.SearchQuery.Container>
                <St.SearchQuery.Wrapper>
                    <div className='modeToggle'>
                        <button
                            type='button'
                            className={!isScanningMode ? 'active' : ''}
                            onClick={() => handleChangeIsScanningMode(false)}
                        >⌨ 입력모드</button>
                        <button
                            type='button'
                            className={isScanningMode ? 'active' : ''}
                            onClick={() => handleChangeIsScanningMode(true)}
                        >📷 스캔모드</button>
                    </div>
                    <div className='inputField'>
                        {isScanningMode ?
                            <div className={`scannedResult ${!searchQuery ? 'spaceField' : ''}`}>
                                <span className='scanDot' />
                                {searchQuery || '바코드를 스캔하세요'}
                            </div>
                            :
                            <input
                                type='text'
                                placeholder='운송장, 상품명, 옵션명, 옵션코드'
                                value={searchQuery || ''}
                                onChange={handleChangeSearchQuery}
                                autoFocus
                            />
                        }
                        {searchQuery &&
                            <button
                                type='button'
                                className='clearBtn'
                                onClick={() => handleClearSearchQuery()}
                                aria-label='검색어 초기화'
                            >✕</button>
                        }
                    </div>
                </St.SearchQuery.Wrapper>
            </St.SearchQuery.Container>
            <St.MoveBar.Container className={selectedItemUUIDs.size > 0 ? 'visible' : ''}>
                <div className='count'>{selectedItemUUIDs.size}개 선택됨</div>
                <div className='actions'>
                    <button
                        type='button'
                        className='clear'
                        onClick={() => handleClearItemSelection()}
                    >
                        선택 해제
                    </button>
                    <button
                        type='button'
                        className='move'
                        onClick={() => handleOpenAssignModalForSelected()}
                    >
                        확정 지정
                    </button>
                </div>
            </St.MoveBar.Container>
            <St.Table.Container>
                <St.Table.Wrapper>
                    <St.Table.StyledTable>
                        <St.Table.ColumnGroup>
                            <col width={34} />
                            <col width={200} />
                            <col width={40} />
                            <col width={90} />
                        </St.Table.ColumnGroup>
                        <St.Table.TableHeader>
                            <St.Table.HeaderRow>
                                <St.Table.HeaderCell style={{ textAlign: 'center' }}>
                                    <input
                                        type='checkbox'
                                        checked={allFilteredChecked}
                                        onChange={() => handleToggleSelectAllFiltered()}
                                    />
                                </St.Table.HeaderCell>
                                <St.Table.HeaderCell>준비/확정 · 상품명 / 옵션명</St.Table.HeaderCell>
                                <St.Table.HeaderCell>박스<br />수량</St.Table.HeaderCell>
                                <St.Table.HeaderCell>파레트 확정</St.Table.HeaderCell>
                            </St.Table.HeaderRow>
                        </St.Table.TableHeader>
                        <St.Table.TableBody>
                            {filteredLoadItems?.map((loadItem, index) => {
                                const isSameProductAndOption = index > 0 &&
                                    loadItem.productName === loadItems[index - 1].productName &&
                                    loadItem.optionName === loadItems[index - 1].optionName &&
                                    loadItem.palletNumber === loadItems[index - 1].palletNumber
                                    ;

                                const countOfPalletAndProductNameAndOptionName = loadItems.filter(r => r.palletNumber === loadItem.palletNumber && r.productName === loadItem.productName && r.optionName === loadItem.optionName && r.boxQuantity > 0).length;
                                const countOfPalletAndProductName = loadItems.filter(r => r.palletNumber === loadItem.palletNumber && r.productName === loadItem.productName && r.boxQuantity > 0).length;
                                const subIndex = isSameProductAndOption ? loadItems.slice(0, index).filter(item => item.productName === loadItem.productName && item.optionName === loadItem.optionName && item.palletNumber === loadItem.palletNumber && loadItem.boxQuantity > 0).length : 0;
                                return <Row
                                    key={loadItem?.uuid}
                                    loadItem={loadItem}
                                    subIndex={subIndex}
                                    countOfPalletAndProductNameAndOptionName={countOfPalletAndProductNameAndOptionName}
                                    countOfPalletAndProductName={countOfPalletAndProductName}
                                    onOpenAssignModal={handleOpenAssignModal}
                                    onUnconfirm={handleUnconfirm}
                                    isChecked={selectedItemUUIDs.has(loadItem?.uuid)}
                                    onToggleCheck={handleToggleItemCheck}
                                    onOpenBulkSelectModal={handleOpenBulkSelectModal}
                                />
                            })}
                        </St.Table.TableBody>
                    </St.Table.StyledTable>
                </St.Table.Wrapper>
            </St.Table.Container>
            <St.CountField.Container>
                <div>미확정 : {filteredLoadItems?.filter(r => r.confirmedYn !== 'y')?.length}</div>
                <div>확정 : {filteredLoadItems?.filter(r => r.confirmedYn === 'y')?.length}</div>
            </St.CountField.Container>

            {selectPalletModalOpen &&
                <SelectPalletModal
                    open={selectPalletModalOpen}
                    onClose={handleCloseSelectPalletUUIDModal}
                    isConfirmAxis={isConfirmAxis}
                    pallets={isConfirmAxis ? sortedConfirmedPallets : sortedPallets}
                    selectedPalletUUIDs={isConfirmAxis ? selectedConfirmedPalletUUIDs : selectedPalletUUIDs}
                    onPushSelectedPalletUUID={isConfirmAxis ? handlePushSelectedConfirmedPalletUUID : handlePushSelectedPalletUUID}
                />
            }

            {printModeModalOpen &&
                <PrintModeModal
                    open={printModeModalOpen}
                    onClose={handlePrintModeModalClose}
                    loadItems={filteredLoadItems}
                />
            }

            {bulkSelectTarget &&
                <WegetBulkSelectModal
                    open={!!bulkSelectTarget}
                    onClose={handleCloseBulkSelectModal}
                    productName={bulkSelectTarget?.productName}
                    optionName={bulkSelectTarget?.optionName}
                    totalCount={bulkSelectPool.length}
                    selectedCount={bulkSelectPool.length - bulkSelectableItems.length}
                    selectableCount={bulkSelectableItems.length}
                    onConfirm={handleConfirmBulkSelect}
                />
            }

            {assignModalOpen &&
                <AssignConfirmModal
                    open={assignModalOpen}
                    onClose={handleCloseAssignModal}
                    confirmedPallets={sortedConfirmedPallets}
                    selectedCount={assignTargetItems.length}
                    onAssign={handleAssignToConfirmed}
                />
            }
        </>
    );
}

const Row = memo(({ loadItem, onOpenAssignModal, onUnconfirm, subIndex, countOfPalletAndProductNameAndOptionName, countOfPalletAndProductName, isChecked, onToggleCheck, onOpenBulkSelectModal }) => {
    const isConfirmed = loadItem?.confirmedYn === 'y';
    return (
        <St.Table.TableRow
            className={`${isConfirmed ? 'loadedY' : 'loadedN'} ${isChecked ? 'checked' : ''} selectable`}
            onClick={() => onToggleCheck(loadItem)}
        >
            <St.Table.TableCell style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                <input
                    type='checkbox'
                    checked={isChecked}
                    onChange={() => onToggleCheck(loadItem)}
                />
            </St.Table.TableCell>
            <St.Table.TableCell>
                <div className='flex gap-2'>
                    <div className='palletBadges'>
                        <St.Table.PrepBadge>{loadItem?.palletNumber ?? '-'}</St.Table.PrepBadge>
                        {isConfirmed &&
                            <St.Table.ConfirmedBadge>확정 {loadItem?.confirmedPalletNumber}</St.Table.ConfirmedBadge>
                        }
                    </div>
                    <div>
                        <div>{loadItem?.productName}</div>
                        <div>{loadItem?.optionName}</div>
                        <St.Table.BulkSelectButton
                            type='button'
                            onClick={(e) => { e.stopPropagation(); onOpenBulkSelectModal(loadItem); }}
                        >
                            동일 품목 선택
                        </St.Table.BulkSelectButton>
                    </div>
                    <div style={{ color: 'var(--purpleColor1)' }}>{loadItem.boxQuantity <= 0 ? <span style={{ color: 'red' }}>N</span> : subIndex + 1} / {countOfPalletAndProductNameAndOptionName}<span style={{ color: '#000000' }}>, T:{countOfPalletAndProductName}</span></div>
                </div>
            </St.Table.TableCell>
            <St.Table.TableCell>
                {loadItem?.boxQuantity}
            </St.Table.TableCell>
            <St.Table.TableCell>
                {isConfirmed ?
                    <St.Table.ConfirmButton
                        type="button"
                        style={{ width: 'auto', padding: '0 10px' }}
                        $palletConfirmYn="y"
                        onClick={(e) => { e.stopPropagation(); onUnconfirm([loadItem]); }}
                    >
                        취소
                    </St.Table.ConfirmButton>
                    :
                    <St.Table.ConfirmButton
                        type="button"
                        style={{ width: 'auto', padding: '0 10px' }}
                        $palletConfirmYn="n"
                        onClick={(e) => { e.stopPropagation(); onOpenAssignModal([loadItem]); }}
                    >
                        확정 지정
                    </St.Table.ConfirmButton>
                }
            </St.Table.TableCell>
        </St.Table.TableRow>
    );
});

function SelectPalletModal({
    open,
    onClose,
    isConfirmAxis,
    pallets,
    selectedPalletUUIDs,
    onPushSelectedPalletUUID
}) {
    return (
        <CustomDialog open={open} onClose={() => onClose()}>
            <St.SelectPalletModal.Header>
                <CustomDialog.Header>
                    <CustomDialog.Header.Fake />
                    <CustomDialog.Header.Title>{isConfirmAxis ? '확정 파레트 선택' : '준비 파레트 선택'}</CustomDialog.Header.Title>
                    <CustomDialog.Header.Close onClick={() => onClose()} />
                </CustomDialog.Header>
            </St.SelectPalletModal.Header>
            <St.SelectPalletModal.Body>
                <div className='gridWrapper'>
                    {isConfirmAxis &&
                        <div
                            className={`item ${selectedPalletUUIDs?.includes(UNCONFIRMED_KEY) ? 'selected' : ''}`}
                            onClick={() => onPushSelectedPalletUUID(UNCONFIRMED_KEY)}
                        >미확정</div>
                    }
                    {pallets?.map(pallet => {
                        return (
                            <div
                                key={pallet.uuid}
                                className={`item ${selectedPalletUUIDs?.includes(pallet.uuid) ? 'selected' : ''}`}
                                onClick={() => onPushSelectedPalletUUID(pallet.uuid)}
                            >{pallet.palletNumber}</div>
                        );
                    })}
                </div>
            </St.SelectPalletModal.Body>
        </CustomDialog>
    );
}

// 확정 파레트 배정 모달. 기존 확정 파레트 선택 또는 새 확정 파레트 생성.
function AssignConfirmModal({
    open,
    onClose,
    confirmedPallets,
    selectedCount,
    onAssign
}) {
    return (
        <CustomDialog open={open} onClose={() => onClose()}>
            <St.ChangePalletNumberModal.Header>
                <CustomDialog.Header>
                    <CustomDialog.Header.Fake />
                    <CustomDialog.Header.Title>선택 {selectedCount}개 확정 파레트 지정</CustomDialog.Header.Title>
                    <CustomDialog.Header.Close onClick={() => onClose()} />
                </CustomDialog.Header>
            </St.ChangePalletNumberModal.Header>
            <St.ChangePalletNumberModal.Body>
                <div className='gridWrapper'>
                    <div className='item' style={{ background: '#f0fdf4', border: '1px dashed #86efac' }}>
                        <CustomBlockButton
                            type='button'
                            onClick={() => onAssign(null, true)}
                        >새 확정 파레트로 지정</CustomBlockButton>
                        <div style={{ color: '#16a34a' }}>NEW</div>
                    </div>
                    {confirmedPallets?.map(pallet => {
                        return (
                            <div
                                key={pallet.uuid}
                                className='item'
                            >
                                <div>
                                    {pallet.palletNumber}
                                </div>
                                <CustomBlockButton
                                    type='button'
                                    onClick={() => onAssign(pallet, false)}
                                >지정</CustomBlockButton>
                            </div>
                        );
                    })}
                </div>
            </St.ChangePalletNumberModal.Body>
        </CustomDialog>
    );
}

function PrintModeModal({
    open,
    onClose,
    loadItems
}) {
    const printRef = useRef(null);

    const handlePrint = useReactToPrint({
        contentRef: printRef,
        preserveLayout: true,
    });

    return (
        <CustomDialog open={open} onClose={() => onClose()} maxWidth='lg'>
            <St.PrintModeModal.Header>
                <CustomDialog.Header>
                    <CustomDialog.Header.Fake />
                    <CustomDialog.Header.Title>프린트 모드</CustomDialog.Header.Title>
                    <CustomDialog.Header.Close onClick={() => onClose()} />
                </CustomDialog.Header>
            </St.PrintModeModal.Header>
            <St.PrintModeModal.Body>
                <div>
                    <CustomBlockButton
                        type='button'
                        className='printButton'
                        onClick={() => handlePrint()}
                    >
                        프린트 출력
                    </CustomBlockButton>
                </div>
                <St.PrintModeModal.TableContainer ref={printRef}>
                    <div className='wrapper'>
                        <table className='styledTable'>
                            <colgroup>
                                <col width={300} />
                                <col width={50} />
                                <col width={100} />
                                <col width={300} />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th>파레트 / 상품명 / 옵션명</th>
                                    <th>박스<br />수량</th>
                                    <th>운송장</th>
                                    <th>메모</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loadItems?.map((loadItem, index) => {
                                    const isSameProductAndOption = index > 0 &&
                                        loadItem.productName === loadItems[index - 1].productName &&
                                        loadItem.optionName === loadItems[index - 1].optionName &&
                                        loadItem.palletNumber === loadItems[index - 1].palletNumber
                                        ;

                                    const countOfPalletAndProductNameAndOptionName = loadItems.filter(r => r.palletNumber === loadItem.palletNumber && r.productName === loadItem.productName && r.optionName === loadItem.optionName && r.boxQuantity > 0).length;
                                    const countOfPalletAndProductName = loadItems.filter(r => r.palletNumber === loadItem.palletNumber && r.productName === loadItem.productName && r.boxQuantity > 0).length;
                                    const subIndex = isSameProductAndOption ? loadItems.slice(0, index).filter(item => item.productName === loadItem.productName && item.optionName === loadItem.optionName && item.palletNumber === loadItem.palletNumber && loadItem.boxQuantity > 0).length : 0;
                                    return (
                                        <tr className={`${loadItem?.confirmedYn === 'y' ? 'loadedY' : 'loadedN'}`} key={loadItem?.uuid}>
                                            <td>
                                                <div className='flex gap-2'>
                                                    <div className='palletNumber'>{loadItem?.palletNumber}</div>
                                                    <div>
                                                        <div>{loadItem?.productName}</div>
                                                        <div>{loadItem?.optionName}</div>
                                                    </div>
                                                    {loadItem.boxQuantity > 0 &&
                                                        <div style={{ color: 'var(--purpleColor1)' }}>{loadItem.boxQuantity <= 0 ? <span style={{ color: 'red' }}>N</span> : subIndex + 1} / {countOfPalletAndProductNameAndOptionName}<span style={{ color: '#000000' }}>, G.T:{countOfPalletAndProductName}</span></div>
                                                    }
                                                </div>
                                            </td>
                                            <td>
                                                {loadItem?.boxQuantity}
                                            </td>
                                            <td>
                                                {loadItem?.waybillNumber?.split('\n')[0]}
                                            </td>
                                            <td>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                </St.PrintModeModal.TableContainer>
            </St.PrintModeModal.Body>
        </CustomDialog>
    );
}
